import React, { useState, useEffect } from "react";
import { Loader } from "../../../../common/components/loader";
import SelectDateRange from "../../../../common/components/selectDateRange";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import Utils from "../../../../utility";
import CustomPagination from "../../../../common/components/customPagination";
import { FaArrowUp } from "react-icons/fa";
import { keyConstants } from "../../../../constants";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

const PREFIX = "EmailDatabase";

const classes = {
  tableHead: `${PREFIX}-tableHead`,
  tableBody: `${PREFIX}-tableBody`
};

const Root = styled("div")({
  [`& .${classes.tableHead}`]: {
    "& .MuiTableCell-head": {
      padding: "10px 0px 10px 30px"
    }
  },
  [`& .${classes.tableBody}`]: {
    "& .MuiTableCell-body": {
      padding: "10px 0px 10px 30px"
    }
  }
});

function EmailDatabase({
  marketingUsers,
  discounts,
  searchQuery,
  setSearchQuery,
  discount,
  setDiscount,
  dates,
  setDates,
  calenderOpen,
  setCalenderOpen,
  isAscending,
  setIsAscending,
  isLoading,
  page,
  setPage,
  itemsPerPage,
  totalItems,
  discountDropdownRef,
  calendarDropDownRef,
  dataCountTillPage,
  handleExport,
  setIsFilterApplied,
  setIsApplyCount,
  isApplyCount
}) {
  const [isDiscountDropdownOpen, setIsDiscountDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        calendarDropDownRef.current &&
        !calendarDropDownRef.current.contains(event.target)
      ) {
        setCalenderOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        discountDropdownRef.current &&
        !discountDropdownRef.current.contains(event.target)
      ) {
        setIsDiscountDropdownOpen(false); // Close font dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutsideDropdown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const toggleDropdown = () => {
    setIsDiscountDropdownOpen(!isDiscountDropdownOpen);
  };

  const handleDiscount = (discount) => {
    setDiscount(discount);
    setPage(1);
    setIsDiscountDropdownOpen(false);
  };

  const renderDiscountText = (discount) => {
    if (!discount) return "Discount";
    return discount !== keyConstants.ALL_DISCOUNTS
      ? Utils.isPurelyNumeric(discount)
        ? `Discount ${discount}%`
        : discount
      : `${keyConstants.ALL_DISCOUNTS}`;
  };

  // Function to render the loader component
  const renderLoader = () => (
    <div
      style={{ height: window.innerHeight - 269 }}
      className="flex flex-col items-center gap-9"
    >
      <Loader modifyLoader={"text-purple-6C36B0"} />
    </div>
  );

  // Function to render the table content
  const renderTableContent = () => (
    <div className="relative mt-8 overflow-auto rounded border border-b-0 border-grey-D9D9D9">
      <TableContainer className="w-full whitespace-nowrap text-left">
        <Table>
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCell>
                <p className="font-MontserratSemiBold text-ft16-19.5">S.No</p>
              </TableCell>
              <TableCell>
                <p className="font-MontserratSemiBold text-ft16-19.5">
                  Email ID
                </p>
              </TableCell>
              <TableCell style={{ display: "flex" }}>
                <p className="font-MontserratSemiBold text-ft16-19.5">
                  Submitted On
                </p>
                <div className="pl-2">
                  <FaArrowUp
                    className={`h-5 w-13px cursor-pointer text-purple-6C36B0 duration-100 ${
                      isAscending ? "" : "-rotate-180" // Add rotation class conditionally
                    }`}
                    onClick={() => setIsAscending(!isAscending)}
                  />
                </div>
              </TableCell>
              <TableCell>
                <p className="pr-5 font-MontserratSemiBold text-ft16-19.5">
                  Discount
                </p>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {marketingUsers?.map((item, index) => (
              <TableRow key={item?._id} className={classes.tableBody}>
                <TableCell>
                  <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                    {dataCountTillPage + index + 1}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                    {item.email}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="font-PoppinsRegular text-ft15-26.25 text-black-000000">
                    {Utils.formatTimestamp(item.addedOn)}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="pr-5 font-PoppinsRegular text-ft15-26.25 text-black-000000">
                    {item.discountValue
                      ? Utils.isPurelyNumeric(item.discountValue)
                        ? `${item.discountValue}%`
                        : item.discountValue
                      : "N/A"}
                  </p>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  // Function to render the no email submitted message
  const renderNoEmailMessage = () => (
    <div
      style={{ height: window.innerHeight - 269 }}
      className="mt-8 grid w-full place-items-center rounded border border-solid border-grey-D9D9D9"
    >
      <div className="flex flex-col items-center gap-9">
        <img src="/images/no-email-submit.svg" alt="No Email Submitted" />
        <div className="font-PoppinsMedium text-ft22-33-0.4 text-grey-767676">
          No Email Submitted Yet
        </div>
      </div>
    </div>
  );

  // Main component rendering logic
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    } else if (marketingUsers?.length > 0) {
      return renderTableContent();
    } else {
      return renderNoEmailMessage();
    }
  };

  return (
    <Root className="mb-20 mt-7.5 flex w-full flex-col px-4 md:pl-12.5 md:pr-7.5">
      <div className="md:flex md:flex-row md:justify-between">
        <div>
          <p className="font-PoppinsSemiBold text-ft22-33-0.4 text-black-20">
            Email Database
          </p>
          <p className="font-PoppinsRegular text-ft15-22.5-0.3 text-grey-575757">
            List of emails submitted by users for marketing purpose
          </p>
        </div>
        <div>
          <button
            onClick={handleExport}
            className="mt-15px h-9.5 w-121px rounded bg-purple-6C36B0 md:mt-0"
          >
            <span className="font-MontserratMedium text-ft15-26.25 text-white">
              Export
            </span>
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-col flex-wrap items-center gap-15px md:mt-8 md:flex-row lg:flex-nowrap">
        <div className="flex h-10 w-full items-center gap-3 rounded border border-grey-D9D9D9 px-3 py-7px md:w-241px">
          <img src="/images/search-icon.svg" alt="search" />
          <input
            type="text"
            value={searchQuery}
            name="search"
            placeholder="Search"
            className="size-full border-none font-MontserratMedium text-ft15-26.25 outline-none placeholder:text-black-000000"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="w-full md:w-241px">
          <SelectDateRange
            dates={dates}
            setDates={setDates}
            placeHolder={"Date"}
            className="font-MontserratMedium text-ft15-26.25 text-black-000000"
            calenderOpen={calenderOpen}
            setCalenderOpen={setCalenderOpen}
            id="emailDatabase"
            setPage={setPage}
            setIsFilterApplied={setIsFilterApplied}
            calendarDropDownRef={calendarDropDownRef}
            setIsApplyCount={setIsApplyCount}
            isApplyCount={isApplyCount}
          />
        </div>
        <div ref={discountDropdownRef} className="relative w-full md:w-241px">
          <div
            className="z-10 flex h-10 w-full cursor-pointer items-center justify-between rounded border border-grey-D9D9D9 p-3 font-MontserratMedium text-ft15-26.25 text-black-000000"
            onClick={toggleDropdown}
          >
            {renderDiscountText(discount)}
            <span
              className={`${discount && "hidden"} ml-auto pr-2 font-MontserratMedium text-ft15-26.25 text-black-000000 `}
            >
              All
            </span>
            <img
              className={`${
                isDiscountDropdownOpen && "-rotate-180"
              }  text-purple-6C36B0 duration-100`}
              src="/images/dropdown-arrow-purple.svg"
            />
          </div>
          {isDiscountDropdownOpen && discounts?.length > 0 && (
            <ul
              className="absolute z-20 mt-9px max-h-64 w-full overflow-y-auto rounded border border-grey-D9D9D9 bg-white md:w-241px"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#D6D6D6 #FFFFFF"
              }}
            >
              {discounts?.map((discount) => (
                <li
                  key={`${discount} - discount`.toLocaleLowerCase()}
                  className="cursor-pointer px-4 py-2 font-MontserratMedium text-ft15-26.25 text-black-000000 hover:rounded hover:bg-purple-6C36B0 hover:text-white"
                  onClick={() => handleDiscount(discount)}
                >
                  {Utils.isPurelyNumeric(discount) ? `${discount}%` : discount}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {renderContent()}

      {totalItems > itemsPerPage && (
        <CustomPagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={page}
          handleChangePage={handleChangePage}
        />
      )}
    </Root>
  );
}
EmailDatabase.propTypes = {
  discounts: PropTypes.arrayOf(PropTypes.object), // Define the shape if you know it
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  discount: PropTypes.number, // Define as required if needed
  setDiscount: PropTypes.func.isRequired,
  dates: PropTypes.arrayOf(PropTypes.object), // Define the shape if you know it
  setDates: PropTypes.func.isRequired,
  calenderOpen: PropTypes.bool.isRequired,
  setCalenderOpen: PropTypes.func.isRequired,
  isAscending: PropTypes.bool.isRequired,
  setIsAscending: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  discountDropdownRef: PropTypes,
  calendarDropDownRef: PropTypes,
  dataCountTillPage: PropTypes.number,
  handleExport: PropTypes.func.isRequired,
  setIsFilterApplied: PropTypes.func.isRequired,
  setIsApplyCount: PropTypes.func.isRequired,
  isApplyCount: PropTypes.bool.isRequired,
  marketingUsers: PropTypes
};
export default EmailDatabase;
